/**
 * Injeção do snippet do Zendesk (Messenger), com a guarda da invariante "um
 * widget por página".
 *
 * A guarda evita reaproveitar um `<script id="ze-snippet">` de outro widget
 * (ex.: injetado por GTM ou por uma página legada): o Messenger herdaria a key
 * errada e as APIs esperadas não existiriam (`messenger.* does not exist`,
 * visto na POC — spike PF-4439 no Jira).
 *
 * IMPORTANTE — remover a tag `<script>` NÃO desfaz o `window.zE` nem os
 * iframes que o SDK já criou. Por isso a guarda abaixo checa não só a tag, mas
 * também `window.zE`: se houver um widget vivo na página sem a nossa tag,
 * recusamos injetar por cima (só um reload limpa o SDK).
 */

const SNIPPET_ID = 'ze-snippet';
const MODE_ATTR = 'data-mandae-zendesk-mode';
const SNIPPET_BASE_URL = 'https://static.zdassets.com/ekr/snippet.js';

/** Marcador gravado na tag para distinguir o nosso snippet de outro widget. */
const MESSENGER_MODE = 'messenger';

/**
 * Injeta o snippet do Messenger com a key informada.
 *
 * - Se o nosso snippet já existe, reaproveita (resolve imediatamente).
 * - Se existe um `#ze-snippet` sem o nosso marcador, rejeita: injetar um
 *   segundo widget quebraria os dois. O erro sobe para o componente, que loga e
 *   entra no seu circuito de retry/backoff.
 * - Se NÃO há tag, mas `window.zE` já existe, há um widget vivo de resquício
 *   (a tag foi removida mas o SDK continua carregado). Injetar por cima
 *   reproduz o bug da POC, então também rejeitamos — exige reload.
 */
export function loadZendeskSnippet(key: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (!key) {
      reject(new Error('Key do Zendesk Messenger não configurada'));
      return;
    }

    const existing = document.getElementById(SNIPPET_ID);

    if (existing) {
      const existingMode = existing.getAttribute(MODE_ATTR);

      if (existingMode !== MESSENGER_MODE) {
        reject(
          new Error(
            `Já existe outro widget Zendesk ("${existingMode}") nesta página; ` +
              `recusando injetar o Messenger. Só é permitido um widget por página.`
          )
        );
        return;
      }

      resolve();
      return;
    }

    // Sem a tag, mas com o SDK ainda carregado: widget vivo de resquício.
    if (typeof (window as any).zE !== 'undefined') {
      reject(
        new Error(
          `Já existe um widget Zendesk carregado (window.zE) nesta página sem a tag ` +
            `de snippet; recusando injetar o Messenger. Limpar o SDK exige reload da página.`
        )
      );
      return;
    }

    const script = document.createElement('script');
    script.id = SNIPPET_ID;
    script.setAttribute(MODE_ATTR, MESSENGER_MODE);
    script.src = `${SNIPPET_BASE_URL}?key=${encodeURIComponent(key)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar o snippet do Zendesk Messenger'));

    document.head.appendChild(script);
  });
}

/**
 * Remove o snippet injetado. Fecha o ciclo de vida em SPA: sem isso o
 * `<script>` sobreviveria à destruição do componente.
 */
export function removeZendeskSnippet(): void {
  const script = document.getElementById(SNIPPET_ID);

  if (script && script.parentNode) {
    script.parentNode.removeChild(script);
  }
}
